import { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authContext } from "../contexts/AuthProvider";
import { loaderContext } from "../contexts/LoaderProvider";
import { branchSwitchContext } from "../contexts/BranchSwitchProvider";
import { sidebarContext } from "../contexts/SidebarProvider";
import CurrentBranch from "./CurrentBranch";
import { formatDecimal } from "../utils/decimalFormat";
import {
  BRANCHES_KEY,
  CURRENT_BRANCH_KEY,
  clearBranchCache,
  clearSession,
  readJSON,
} from "../services/session";

const Header = () => {
  const [themeMode, setThemeMode] = useState("");
  const [currentBranch, setCurrentBranch] = useState(
    () => readJSON(CURRENT_BRANCH_KEY) ?? {}
  );
  // eslint-disable-next-line
  const [_, setIsAuth, merchantData] = useContext(authContext);
  const branchList = merchantData?.branchList ?? [];
  const branches = branchList.length > 1 ? branchList : [];
  const { setIsLoading } = useContext(loaderContext);
  const { open, setOpen } = useContext(sidebarContext);
  const { setBranchSwitch } = useContext(branchSwitchContext);
  const navigate = useNavigate();
  const manageTheme = (mode) => {
    localStorage.setItem("data-bs-theme", mode);
    navigate(0);
  };
  const selectBranch = (branch) => {
    setIsLoading(true);
    const allBranches = [...branches];
    setCurrentBranch(branch);
    localStorage.setItem(CURRENT_BRANCH_KEY, JSON.stringify(branch));
    localStorage.setItem(BRANCHES_KEY, JSON.stringify(allBranches));
    setBranchSwitch(branch);
    setIsLoading(false);
  };
  const logout = () => {
    clearSession();
    setIsAuth(false);
    window.location.replace("/");
  };
  useEffect(() => {
    if (!merchantData?.branchList) return;
    const list = merchantData.branchList;
    const stored = readJSON(CURRENT_BRANCH_KEY);
    const match = list.find((item) => item?._id === stored?._id);
    if (match) {
      setCurrentBranch(match);
      return;
    }
    const fallback =
      list.find((item) => item?._id === merchantData?.branchId) ?? list[0];
    setCurrentBranch(fallback ?? {});
    if (fallback) {
      localStorage.setItem(CURRENT_BRANCH_KEY, JSON.stringify(fallback));
    } else {
      clearBranchCache();
    }
  }, [merchantData]);
  useEffect(() => {
    const theme = localStorage.getItem("data-bs-theme");
    const html = document.documentElement;
    html.setAttribute("data-theme-mode", theme);
    html.setAttribute("data-header-styles", theme);
    html.setAttribute("data-menu-styles", theme);
    setThemeMode(theme);
  }, [navigate]);
  return (
    <header className="app-header sticky" id="header">
      {/* Start::main-header-container */}
      <div className="main-header-container container-fluid">
        {/* Start::header-content-left */}
        <div className="header-content-left">
          {/* Start::header-element */}
          <div className="header-element">
            <div className="horizontal-logo">
              <Link to={`/dashboard`} className="header-logo">
                <img
                  src="assets/images/brand-logos/toggle-logo.png"
                  alt="logo"
                  className="desktop-logo"
                />
                <img
                  src="assets/images/brand-logos/toggle-dark.png"
                  alt="logo"
                  className="toggle-dark"
                />
                <img
                  src="assets/images/brand-logos/toggle-dark.png"
                  alt="logo"
                  className="desktop-dark"
                />
                <img
                  src="assets/images/brand-logos/toggle-logo.png"
                  alt="logo"
                  className="toggle-logo"
                />
                <img
                  src="assets/images/brand-logos/toggle-white.png"
                  alt="logo"
                  className="toggle-white"
                />
                <img
                  src="assets/images/brand-logos/toggle-white.png"
                  alt="logo"
                  className="desktop-white"
                />
              </Link>
            </div>
          </div>
          {/* End::header-element */}

          {/* Start::header-element */}
          <div className="header-element mx-lg-0 mx-2">
            <span
              onClick={() => {
                const html = document.documentElement;
                html.setAttribute("data-toggled", "open");
                setOpen(!open);
              }}
              aria-label="Hide Sidebar"
              className="sidemenu-toggle header-link animated-arrow hor-toggle horizontal-navtoggle"
            >
              <span></span>
            </span>
          </div>
          {/* End::header-element */}
        </div>
        {/* End::header-content-left */}

        {/* Start::header-content-right */}
        <ul className="header-content-right">
          {/* Start::header-element */}
          <li className="header-element dropdown">
            {/* Start::header-link|dropdown-toggle */}
            <span
              className="header-link cursor-pointer dropdown-toggle"
              id="mainHeaderProfile"
              data-bs-toggle={branches?.length > 0 ? `dropdown` : ``}
              aria-expanded="false"
            >
              <div className="d-flex align-items-center">
                <div className="dropdown-item">
                  <CurrentBranch branch={currentBranch?.name} />
                </div>
              </div>
            </span>
            {/* End::header-link|dropdown-toggle */}
            {branches?.length > 0 ? (
              <ul
                className="main-header-dropdown dropdown-menu pt-0 overflow-hidden header-profile-dropdown dropdown-menu-end"
                aria-labelledby="mainHeaderProfile"
              >
                {branches?.map((branch, index) => {
                  return (
                    <li
                      key={index}
                      onClick={() => {
                        selectBranch(branch);
                      }}
                    >
                      <span
                        className={`dropdown-item d-flex cursor-pointer align-items-center ${currentBranch?.name === branch?.name &&
                          "bg-primary text-light"
                          }`}
                      >
                        {branch?.name}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              ""
            )}
          </li>
          {/* End::header-element */}
          {/* Start::header-element */}
          <li className="header-element">
            <span className="header-link layout-setting">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                className="w-6 h-6 header-link-icon"
                fill="none"
                stroke="currentColor"
              >
                <path d="M20 7V5c0-1.103-.897-2-2-2H5C3.346 3 2 4.346 2 6v12c0 2.201 1.794 3 3 3h15c1.103 0 2-.897 2-2V9c0-1.103-.897-2-2-2zm-2 9h-2v-4h2v4zM5 7a1.001 1.001 0 0 1 0-2h13v2H5z"></path>
              </svg>
              <Link
                to={`/wallet`}
                className="fw-bold text-nowrap"
                style={{ lineHeight: "1rem", color: "currentcolor" }}
              >
                {formatDecimal(merchantData?.merchantBalance)}{" "}
                {merchantData?.merchantCurrency ?? ""}
              </Link>
            </span>
          </li>
          {/* End::header-element */}

          {/* Start::header-element */}
          <li className="header-element header-theme-mode">
            {/* Start::header-link|layout-setting */}
            <span className="header-link layout-setting">
              {themeMode === "light" ? (
                <span
                  onClick={() => {
                    manageTheme("dark");
                  }}
                >
                  {/* Start::header-link-icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6 header-link-icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"
                    />
                  </svg>
                  {/* End::header-link-icon */}
                </span>
              ) : (
                ""
              )}
              {themeMode === "dark" ? (
                <span
                  onClick={() => {
                    manageTheme("light");
                  }}
                >
                  {/* Start::header-link-icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6 header-link-icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
                    />
                  </svg>
                  {/* End::header-link-icon */}
                </span>
              ) : (
                ""
              )}
            </span>
            {/* End::header-link|layout-setting */}
          </li>
          {/* End::header-element */}

          {/* Start::header-element */}
          <li className="header-element dropdown">
            {/* Start::header-link|dropdown-toggle */}
            <span
              className="header-link cursor-pointer dropdown-toggle"
              id="mainHeaderProfile"
              data-bs-toggle="dropdown"
              data-bs-auto-close="outside"
              aria-expanded="false"
            >
              <div className="d-flex align-items-center">
                {merchantData?.merchantLogo && (
                  <img
                    src={`${process.env.REACT_APP_API_PUBLIC_URL}${merchantData?.merchantLogo}`}
                    alt="img"
                    className="avatar custom-header-avatar avatar-rounded"
                  />
                )}
              </div>
            </span>
            {/* End::header-link|dropdown-toggle */}
            <ul
              className="main-header-dropdown dropdown-menu pt-0 overflow-hidden header-profile-dropdown dropdown-menu-end"
              aria-labelledby="mainHeaderProfile"
            >
              <li>
                <div className="dropdown-item text-center border-bottom">
                  <span className="fw-medium">{merchantData?.name ?? ""}</span>
                  <span className="d-block fs-12 text-muted">
                    {merchantData?.role ?? ""}
                  </span>
                </div>
              </li>
              <li>
                <Link
                  className="dropdown-item d-flex align-items-center"
                  to={`/settings`}
                >
                  <i className="ri-settings-5-line lh-1 p-1 rounded-circle bg-primary-transparent text-primary me-2 fs-14"></i>
                  Settings
                </Link>
              </li>
              <li>
                <span
                  className="dropdown-item d-flex align-items-center profile-dropdown-logout"
                  onClick={logout}
                >
                  <i className="ri-door-lock-line lh-1 p-1 rounded-circle bg-primary-transparent text-primary me-2 fs-14"></i>
                  Log Out
                </span>
              </li>
            </ul>
          </li>
          {/* End::header-element */}
        </ul>
        {/* End::header-content-right */}
      </div>
      {/* End::main-header-container */}
    </header>
  );
};

export default Header;
